(() => {
  const config = window.WobPromotionConfig || {};
  if (!config.enabled || !config.endpoint) return;

  const state = {
    promotion: null,
    serverOffset: 0,
    eligibility: new Map(),
    resolvePromise: null,
    boundaryTimer: null,
    countdownTimer: null,
    statusTimer: null
  };
  const endpoint = String(config.endpoint).replace(/\/$/, '');
  const locale = config.locale || document.documentElement.lang || 'en-US';
  const currency = config.currency || 'USD';
  const numericId = (value) => String(value || '').split('/').pop() || '';
  const keyFor = (productId, variantId) => `${numericId(productId)}:${numericId(variantId)}`;
  const now = () => Date.now() + state.serverOffset;
  const baseCompareAt = (container) => Number(
    container.dataset.compareAtPriceCents
    || container.dataset.baseCompareAtCents
    || 0
  );

  const formatMoney = (cents) => new Intl.NumberFormat(locale, {
    style: 'currency',
    currency
  }).format(Number(cents || 0) / 100);

  const ensureElement = (container, selector, tagName, className, dataName) => {
    let element = container.querySelector(selector);
    if (element) return element;
    element = document.createElement(tagName);
    element.className = className;
    element.dataset[dataName] = '';
    container.append(element);
    return element;
  };

  const currentMarkup = (container, cents, formatted = '') => {
    const from = container.dataset.priceShowFrom === 'true'
      ? '<span class="price__from">From</span> '
      : '';
    return `${from}${formatted || formatMoney(cents)}`;
  };

  const updateDiscountLabel = (container, originalPriceCents, currentPriceCents) => {
    const original = Number(originalPriceCents || 0);
    const current = Number(currentPriceCents || 0);
    const percent = original > current && current >= 0
      ? Math.round(((original - current) / original) * 100)
      : 0;
    let label = container.querySelector('[data-price-discount]');

    if (percent <= 0) {
      label?.remove();
      return;
    }

    label = label || ensureElement(container, '[data-price-discount]', 'span', 'price__discount', 'priceDiscount');
    label.textContent = `${percent}% off`;
    label.setAttribute('aria-label', `${percent} percent off`);
    container.append(label);
  };

  const restoreContainer = (container) => {
    const base = Number(container.dataset.basePriceCents || 0);
    const compare = baseCompareAt(container);
    const onSale = compare > base;
    const current = ensureElement(container, '[data-price-current]', 'span', 'price__current', 'priceCurrent');
    const currentLabel = ensureElement(container, '[data-price-current-label]', 'span', 'visually-hidden', 'priceCurrentLabel');
    current.innerHTML = currentMarkup(container, base, container.dataset.basePriceFormatted || '');
    currentLabel.textContent = onSale ? 'Sale price' : 'Regular price';
    container.classList.toggle('price--on-sale', onSale);
    container.classList.remove('price--promotion');
    container.dataset.promotionApplied = 'false';

    let comparePrice = container.querySelector('[data-price-compare]');
    let compareLabel = container.querySelector('[data-price-compare-label]');
    if (onSale) {
      compareLabel = compareLabel || ensureElement(container, '[data-price-compare-label]', 'span', 'visually-hidden', 'priceCompareLabel');
      comparePrice = comparePrice || ensureElement(container, '[data-price-compare]', 's', 'price__compare', 'priceCompare');
      compareLabel.textContent = 'Regular price';
      comparePrice.textContent = container.dataset.compareAtPriceFormatted || formatMoney(compare);
      container.prepend(compareLabel, comparePrice);
    } else {
      comparePrice?.remove();
      compareLabel?.remove();
    }
    updateDiscountLabel(container, compare, base);
  };

  const applyResult = (container, result) => {
    if (!result?.eligible) {
      restoreContainer(container);
      return;
    }
    const current = ensureElement(container, '[data-price-current]', 'span', 'price__current', 'priceCurrent');
    const currentLabel = ensureElement(container, '[data-price-current-label]', 'span', 'visually-hidden', 'priceCurrentLabel');
    const comparePrice = ensureElement(container, '[data-price-compare]', 's', 'price__compare', 'priceCompare');
    const compareLabel = ensureElement(container, '[data-price-compare-label]', 'span', 'visually-hidden', 'priceCompareLabel');
    compareLabel.textContent = 'Original price';
    comparePrice.textContent = formatMoney(result.originalPriceCents);
    currentLabel.textContent = 'Current promotional price';
    current.innerHTML = currentMarkup(container, result.currentPriceCents);
    container.prepend(compareLabel, comparePrice);
    container.append(currentLabel, current);
    container.classList.add('price--on-sale', 'price--promotion');
    container.dataset.promotionApplied = 'true';
    container.dataset.promotionRevision = result.promotionRevision || '';
    updateDiscountLabel(container, result.originalPriceCents, result.currentPriceCents);
  };

  const priceContainers = (root = document) => {
    const descendants = Array.from(root.querySelectorAll?.('[data-product-price][data-product-id]') || []);
    return root instanceof Element && root.matches('[data-product-price][data-product-id]')
      ? [root, ...descendants]
      : descendants;
  };

  const resolveContainers = async (root = document) => {
    const containers = priceContainers(root);
    if (!state.promotion?.active) {
      containers.forEach(restoreContainer);
      updateCountdowns();
      return;
    }
    const unresolved = [];
    containers.forEach((container, index) => {
      const key = keyFor(container.dataset.productId, container.dataset.variantId);
      if (state.eligibility.has(key)) {
        applyResult(container, state.eligibility.get(key));
        return;
      }
      unresolved.push({
        key: `${key}:${index}`,
        cacheKey: key,
        container,
        productId: container.dataset.productId,
        variantId: container.dataset.variantId,
        priceCents: Number(container.dataset.basePriceCents || 0),
        compareAtPriceCents: baseCompareAt(container),
        currencyCode: currency
      });
    });
    if (!unresolved.length) {
      updateCountdowns();
      return;
    }

    const response = await fetch(`${endpoint}/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: unresolved.map(({ container, cacheKey, ...item }) => item) })
    });
    if (!response.ok) throw new Error(`Promotion resolution failed: ${response.status}`);
    const payload = await response.json();
    (payload.items || []).forEach((result, index) => {
      const source = unresolved[index];
      if (!source) return;
      state.eligibility.set(source.cacheKey, result);
      applyResult(source.container, result);
    });
    updateCountdowns();
  };

  const countdownText = (remaining) => {
    const total = Math.max(0, Math.floor(remaining / 1000));
    const days = Math.floor(total / 86400);
    const hours = Math.floor((total % 86400) / 3600);
    const minutes = Math.floor((total % 3600) / 60);
    const seconds = total % 60;
    return days > 0
      ? `${days}d ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      : `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  function updateCountdowns() {
    const endsAt = Date.parse(state.promotion?.manifest?.endsAt || '');
    document.querySelectorAll('[data-promotion-countdown]').forEach((countdown) => {
      const eligibility = state.eligibility.get(keyFor(countdown.dataset.productId, countdown.dataset.variantId));
      const remaining = endsAt - now();
      const visible = Boolean(state.promotion?.active && eligibility?.eligible && Number.isFinite(endsAt) && remaining > 0);
      countdown.hidden = !visible;
      if (!visible) return;
      countdown.querySelector('[data-promotion-countdown-timer]').textContent = countdownText(remaining);
      const accessible = countdown.querySelector('[data-promotion-countdown-accessible]');
      if (accessible) accessible.textContent = `Promotion ends at ${new Date(endsAt).toLocaleString(locale)}.`;
    });
  }

  const scheduleBoundary = () => {
    window.clearTimeout(state.boundaryTimer);
    const phase = state.promotion?.phase;
    const boundary = Date.parse(phase === 'scheduled'
      ? state.promotion?.manifest?.startsAt || ''
      : state.promotion?.manifest?.endsAt || '');
    if (!Number.isFinite(boundary)) return;
    state.boundaryTimer = window.setTimeout(loadPromotion, Math.max(250, Math.min(boundary - now() + 100, 2147483000)));
  };

  const loadPromotion = async () => {
    try {
      const response = await fetch(`${endpoint}/active`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Promotion status failed: ${response.status}`);
      const promotion = await response.json();
      const serverNow = Date.parse(promotion.serverNow || '');
      state.serverOffset = Number.isFinite(serverNow) ? serverNow - Date.now() : 0;
      const revisionChanged = promotion.manifest?.revision !== state.promotion?.manifest?.revision;
      state.promotion = promotion;
      if (revisionChanged) state.eligibility.clear();
      await resolveContainers(document);
      scheduleBoundary();
      window.dispatchEvent(new CustomEvent('wob:promotion-state', { detail: promotion }));
    } catch (error) {
      console.warn('Storefront promotion unavailable; standard prices retained.', error);
      state.promotion = null;
      priceContainers(document).forEach(restoreContainer);
      updateCountdowns();
    }
  };

  const updateVariant = (container, { productId = '', variantId = '', priceCents = 0, compareAtPriceCents = 0 } = {}) => {
    if (!container) return;
    container.dataset.productId = numericId(productId || container.dataset.productId);
    container.dataset.variantId = numericId(variantId);
    container.dataset.basePriceCents = String(priceCents);
    container.dataset.compareAtPriceCents = String(compareAtPriceCents || 0);
    container.dataset.baseCompareAtCents = String(compareAtPriceCents || 0);
    container.dataset.basePriceFormatted = formatMoney(priceCents);
    container.dataset.compareAtPriceFormatted = compareAtPriceCents ? formatMoney(compareAtPriceCents) : '';
    restoreContainer(container);
    resolveContainers(container.parentElement || document).catch(() => {});
    document.querySelectorAll(`[data-promotion-countdown][data-product-id="${numericId(productId || container.dataset.productId)}"]`).forEach((countdown) => {
      countdown.dataset.variantId = numericId(variantId);
    });
  };

  window.WobPromotionPricing = {
    load: loadPromotion,
    refresh: resolveContainers,
    updateVariant,
    getState: () => state.promotion
  };

  document.addEventListener('DOMContentLoaded', () => {
    loadPromotion();
    state.countdownTimer = window.setInterval(updateCountdowns, 1000);
    state.statusTimer = window.setInterval(() => {
      if (!document.hidden) loadPromotion();
    }, 30000);
    const observer = new MutationObserver((mutations) => {
      const hasNewPricing = mutations.some((mutation) => Array.from(mutation.addedNodes).some((node) => (
        node instanceof Element
        && (node.matches?.('[data-product-price]') || node.querySelector?.('[data-product-price]'))
      )));
      if (!hasNewPricing) return;
      window.clearTimeout(state.resolvePromise);
      state.resolvePromise = window.setTimeout(() => resolveContainers(document).catch(() => {}), 50);
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }, { once: true });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) loadPromotion();
  });
})();
